import React, { useState, useEffect } from 'react';
import { qbbAccountApi } from '../../services/api';
import { AdminHeader, PrimaryBtn, GhostBtn, EmptyState } from './AdminLayout';

const MODE_LABEL = { disabled: '券宝宝接口未接入', stub: '本地联调模式', live: '已接入券宝宝' };

// 券宝宝内部账号池：平台自己的一批券宝宝账号，用来把扫码文章发到券宝宝 App。
// 密码加密存后端，token 过期会自动用密码重登，运营平时不用管。
export default function QbbAccounts() {
  const [list, setList] = useState([]);
  const [mode, setMode] = useState('disabled');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ phone: '', password: '', nickname: '', note: '' });
  const [pwEditing, setPwEditing] = useState(null); // 正在改密码的账号
  const [newPw, setNewPw] = useState('');

  const load = () => {
    setLoading(true);
    qbbAccountApi.list()
      .then((d) => { setList(d.list || []); setMode(d.mode || 'disabled'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const doAdd = async () => {
    const phone = '+61' + (form.phone || '').replace(/^0+/, '');
    if (!form.phone || !form.password) { alert('请填手机号和密码'); return; }
    setBusyId('new');
    try {
      const r = await qbbAccountApi.create({ ...form, phone });
      if (r.loginError) alert('账号已保存，但登录失败：' + r.loginError);
      setForm({ phone: '', password: '', nickname: '', note: '' });
      setAdding(false);
      load();
    } catch (e) { alert(e?.response?.data?.error || '添加失败'); }
    finally { setBusyId(''); }
  };

  const doLogin = async (a) => {
    setBusyId(a._id);
    try { await qbbAccountApi.login(a._id); load(); }
    catch (e) { alert(e?.response?.data?.error || '登录失败'); load(); }
    finally { setBusyId(''); }
  };

  const doToggle = async (a) => {
    setBusyId(a._id);
    try { await qbbAccountApi.update(a._id, { status: a.status === 'active' ? 'disabled' : 'active' }); load(); }
    finally { setBusyId(''); }
  };

  const doSavePw = async () => {
    if (!newPw) { alert('请输入新密码'); return; }
    setBusyId(pwEditing._id);
    try {
      const r = await qbbAccountApi.update(pwEditing._id, { password: newPw });
      if (r.loginError) alert('密码已保存，但登录失败：' + r.loginError);
      setPwEditing(null); setNewPw('');
      load();
    } catch (e) { alert(e?.response?.data?.error || '保存失败'); }
    finally { setBusyId(''); }
  };

  const doDelete = async (a) => {
    if (!window.confirm(`确认删除账号 ${a.phone}？`)) return;
    setBusyId(a._id);
    try { await qbbAccountApi.delete(a._id); load(); }
    finally { setBusyId(''); }
  };

  return (
    <div style={styles.page}>
      <AdminHeader
        title="券宝宝账号"
        back={true}
        actions={<PrimaryBtn onClick={() => setAdding(true)}>添加账号</PrimaryBtn>}
      />

      <div style={styles.tip}>
        {MODE_LABEL[mode]}。这些是平台自己的券宝宝账号，扫码文章发到券宝宝 App 时轮换使用；
        密码加密保存，登录状态过期会自动重登。
      </div>

      <div style={styles.body}>
        {loading ? (
          <div style={styles.skeletons}>{[1, 2, 3].map(i => <div key={i} style={styles.skeleton} />)}</div>
        ) : list.length === 0 ? (
          <EmptyState text="还没有内部账号，点右上角添加" />
        ) : (
          <div style={styles.list}>
            {list.map((a) => (
              <div key={a._id} style={styles.card}>
                <div style={styles.cardTop}>
                  <span style={styles.phone}>{a.phone}</span>
                  {a.nickname && <span style={styles.nickname}>{a.nickname}</span>}
                  <span style={a.status === 'active' ? styles.tagOn : styles.tagOff}>
                    {a.status === 'active' ? '启用中' : '已停用'}
                  </span>
                  <span style={a.hasToken ? styles.tokenOk : styles.tokenNo}>
                    {a.hasToken ? '已登录' : '未登录'}
                  </span>
                </div>
                <div style={styles.metaRow}>
                  <span>已发 {a.sentCount} 条</span>
                  <span>{a.lastUsedAt ? `最近使用 ${new Date(a.lastUsedAt).toLocaleString('zh-CN')}` : '未使用过'}</span>
                </div>
                {a.note && <div style={styles.note}>{a.note}</div>}
                {a.lastError && <div style={styles.error}>{a.lastError}</div>}
                <div style={styles.actions}>
                  <GhostBtn small onClick={() => doLogin(a)}>{busyId === a._id ? '处理中...' : '重新登录'}</GhostBtn>
                  <GhostBtn small onClick={() => { setPwEditing(a); setNewPw(''); }}>改密码</GhostBtn>
                  <GhostBtn small onClick={() => doToggle(a)}>{a.status === 'active' ? '停用' : '启用'}</GhostBtn>
                  <GhostBtn small onClick={() => doDelete(a)}>删除</GhostBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {adding && (
        <div style={styles.overlay} onClick={() => setAdding(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>添加券宝宝账号</p>
            <p style={styles.label}>手机号（券宝宝登录号，不含开头的 0）</p>
            <div style={styles.phoneRow}>
              <span style={styles.prefix}>+61</span>
              <input
                style={styles.phoneInput} type="tel" inputMode="numeric"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
              />
            </div>
            <p style={styles.label}>密码</p>
            <input style={styles.input} type="password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <p style={styles.label}>昵称（选填，仅后台展示）</p>
            <input style={styles.input} value={form.nickname}
              onChange={(e) => setForm({ ...form, nickname: e.target.value })} />
            <p style={styles.label}>备注（选填）</p>
            <input style={styles.input} value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} />
            <div style={styles.modalActions}>
              <GhostBtn small onClick={() => setAdding(false)}>取消</GhostBtn>
              <PrimaryBtn small disabled={busyId === 'new'} onClick={doAdd}>
                {busyId === 'new' ? '验证中...' : '添加并登录'}
              </PrimaryBtn>
            </div>
          </div>
        </div>
      )}

      {pwEditing && (
        <div style={styles.overlay} onClick={() => setPwEditing(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>修改密码 · {pwEditing.phone}</p>
            <p style={styles.label}>新密码（需与券宝宝 App 里的一致）</p>
            <input style={styles.input} type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            <div style={styles.modalActions}>
              <GhostBtn small onClick={() => setPwEditing(null)}>取消</GhostBtn>
              <PrimaryBtn small onClick={doSavePw}>保存并登录</PrimaryBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  tip: { background: '#fff', borderBottom: '1px solid #f0f0f0', padding: '10px 14px', fontSize: 12, color: '#6b7280', lineHeight: 1.6 },
  body: { flex: 1, padding: '12px 12px 24px' },
  skeletons: { display: 'flex', flexDirection: 'column', gap: 8 },
  skeleton: { height: 92, borderRadius: 10, background: '#efefef' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  card: { background: '#fff', borderRadius: 10, padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  cardTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  phone: { fontSize: 15, fontWeight: 600, color: '#111' },
  nickname: { fontSize: 12, color: '#6b7280' },
  tagOn: { fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#f0fdf4', padding: '1px 6px', borderRadius: 4 },
  tagOff: { fontSize: 11, fontWeight: 600, color: '#9ca3af', background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 },
  tokenOk: { fontSize: 11, color: '#2563eb', background: '#eff6ff', padding: '1px 6px', borderRadius: 4 },
  tokenNo: { fontSize: 11, color: '#b45309', background: '#fffbeb', padding: '1px 6px', borderRadius: 4 },
  metaRow: { display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: '#9ca3af' },
  note: { marginTop: 6, fontSize: 12, color: '#4b5563' },
  error: { marginTop: 6, fontSize: 12, color: '#ef4444' },
  actions: { display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50 },
  modal: { background: '#fff', borderRadius: 12, padding: 18, width: '100%', maxWidth: 340 },
  modalTitle: { margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#111' },
  label: { margin: '10px 0 6px', fontSize: 12, color: '#6b7280' },
  input: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' },
  phoneRow: { display: 'flex', alignItems: 'stretch', border: '1.5px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' },
  prefix: { display: 'flex', alignItems: 'center', padding: '0 12px', background: '#f5f5f5', color: '#333', fontSize: 14, borderRight: '1.5px solid #e5e7eb' },
  phoneInput: { flex: 1, boxSizing: 'border-box', padding: '10px 12px', border: 'none', fontSize: 14, outline: 'none' },
  modalActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 },
};
